import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Scale, Plus, ArrowRight, Shield, GavelIcon, FileText, Users } from "lucide-react";
import api, { resolveAvatar } from "@/lib/api";

/**
 * Phase 4a — /app/legal
 * Department overview: hero + KPI strip + 18 legal Workforce Members grid +
 * a hint that pushes users into /app/legal/matters and the pricing bundles.
 */
export default function LegalDept() {
    const [agents, setAgents] = useState([]);
    const [hiredSlugs, setHiredSlugs] = useState(new Set());
    const [stats, setStats] = useState({ matters: 0, docs_pending: 0, deadlines_upcoming: 0 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [catalog, hired, matters] = await Promise.all([
                    api.get("/workers/catalog"),
                    api.get("/workers/hired"),
                    api.get("/legal/matters?limit=200"),
                ]);
                const catalogWorkers = catalog?.data?.workers || [];
                const legalAgents = catalogWorkers.filter(
                    (w) => (w.department || "").toLowerCase() === "legal"
                );
                setAgents(legalAgents);
                const hiredList = hired?.data?.hired || [];
                setHiredSlugs(new Set(hiredList.map((h) => h.worker_slug)));

                const items = matters?.data?.items || [];
                const pending = items.reduce((sum, m) => sum + (m.docs_pending || 0), 0);
                const upcoming = items.reduce(
                    (sum, m) => sum + (m.deadlines_upcoming || 0),
                    0
                );
                setStats({
                    matters: items.length,
                    docs_pending: pending,
                    deadlines_upcoming: upcoming,
                });
            } catch (err) {
                // Non-fatal: leave the page usable even if one API fails.
                // Empty state + zero KPIs is the correct fallback.
                console.warn("Legal dept load partial failure:", err?.message);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <div className="h-full overflow-y-auto wfos-scrollbar" data-testid="legal-dept-page">
            <div className="max-w-6xl mx-auto p-8">
                {/* Header */}
                <div className="flex items-start justify-between gap-6">
                    <div>
                        <div className="wfos-label text-gray-500">Departments · Legal</div>
                        <h1 className="mt-2 text-3xl font-medium tracking-tight text-gray-900 flex items-center gap-2">
                            <Scale className="w-7 h-7 text-[#16a34a]" strokeWidth={1.75} />
                            Legal Operations
                        </h1>
                        <p className="mt-2 text-sm text-gray-500 max-w-xl">
                            Supervised AI legal team. Every output is a draft — an attorney of record
                            must review and approve before it can be filed, served, or executed.
                        </p>
                    </div>
                    <Link
                        to="/app/legal/matters/new"
                        data-testid="legal-new-matter-cta"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-[#16a34a] px-4 h-10 text-sm font-medium text-white hover:bg-[#15803d]"
                    >
                        <Plus className="w-4 h-4" strokeWidth={2} /> New matter
                    </Link>
                </div>

                {/* KPI Strip */}
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="legal-stats">
                    <StatTile
                        icon={GavelIcon}
                        label="Active matters"
                        value={stats.matters}
                        tone="green"
                    />
                    <StatTile
                        icon={FileText}
                        label="Pending attorney review"
                        value={stats.docs_pending}
                        tone="amber"
                    />
                    <StatTile
                        icon={Shield}
                        label="Upcoming deadlines"
                        value={stats.deadlines_upcoming}
                        tone="rose"
                    />
                </div>

                {/* Matter list link */}
                <div className="mt-8 wfos-card p-5 flex items-center justify-between">
                    <div>
                        <div className="text-[14.5px] font-semibold text-gray-900">Matter workspace</div>
                        <p className="mt-0.5 text-sm text-gray-500">
                            Manage active matters, ethical walls, deadlines and privileged documents.
                        </p>
                    </div>
                    <Link
                        to="/app/legal/matters"
                        data-testid="legal-open-matters"
                        className="inline-flex items-center gap-1 text-sm font-medium text-[#16a34a] hover:text-[#15803d]"
                    >
                        Open matters <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                    </Link>
                </div>

                {/* Sprint K · Legal Analysis Studio link */}
                <div className="mt-4 wfos-card p-5 flex items-center justify-between">
                    <div>
                        <div className="text-[14.5px] font-semibold text-gray-900 flex items-center gap-2">
                            <GavelIcon className="w-4 h-4 text-[#16a34a]" strokeWidth={2} />
                            Legal Analysis
                        </div>
                        <p className="mt-0.5 text-sm text-gray-500">
                            Multi-step legal analyses: contract breach, statute applicability,
                            compliance review, discovery prep. Attorney-of-record acknowledgment
                            required on every output.
                        </p>
                    </div>
                    <Link
                        to="/app/legal/analysis"
                        data-testid="legal-open-analysis"
                        className="inline-flex items-center gap-1 text-sm font-medium text-[#16a34a] hover:text-[#15803d]"
                    >
                        Open Legal Analysis <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                    </Link>
                </div>

                {/* AI legal team */}
                <div className="mt-10">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="wfos-label text-gray-500">Legal AI team</div>
                            <div className="mt-1 text-[16px] font-semibold text-gray-900">
                                {agents.length} specialized legal agents
                            </div>
                        </div>
                        <Link
                            to="/pricing"
                            className="text-xs font-medium text-[#16a34a] hover:text-[#15803d] inline-flex items-center gap-1"
                            data-testid="legal-view-bundles"
                        >
                            See Legal bundles <ArrowRight className="w-3 h-3" strokeWidth={2} />
                        </Link>
                    </div>

                    {loading ? (
                        <div className="mt-6 text-sm text-gray-500">Loading legal team…</div>
                    ) : (
                        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {agents.map((a) => (
                                <AgentCard
                                    key={a.slug}
                                    agent={a}
                                    hired={hiredSlugs.has(a.slug)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <div className="h-16" />
            </div>
        </div>
    );
}

function StatTile({ icon: Icon, label, value, tone }) {
    const toneMap = {
        green: "bg-[#dcfce7] text-[#16a34a]",
        amber: "bg-amber-100 text-amber-700",
        rose:  "bg-rose-100 text-rose-700",
    };
    return (
        <div className="wfos-card p-4 flex items-center gap-3" data-testid={`legal-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className={`w-9 h-9 rounded-lg grid place-items-center ${toneMap[tone] || "bg-gray-100 text-gray-600"}`}>
                <Icon className="w-4 h-4" strokeWidth={2} />
            </div>
            <div>
                <div className="text-[22px] font-semibold text-gray-900 tabular-nums leading-none">{value}</div>
                <div className="mt-1 text-[11px] uppercase tracking-widest text-gray-500">{label}</div>
            </div>
        </div>
    );
}

function AgentCard({ agent, hired }) {
    return (
        <div
            className="wfos-card p-4 flex items-start gap-3"
            data-testid={`legal-agent-card-${agent.slug}`}
        >
            <img
                src={resolveAvatar(agent.avatar_url)}
                alt={agent.name}
                className="w-12 h-12 rounded-full object-cover border border-[#e8e8e3] flex-shrink-0"
                onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
            />
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <div className="text-[14px] font-semibold text-gray-900 truncate">{agent.name}</div>
                    {hired && (
                        <span className="wfos-pill wfos-pill--green text-[10px] px-1.5 py-0.5">
                            <Users className="w-2.5 h-2.5" strokeWidth={2.5} /> Hired
                        </span>
                    )}
                </div>
                <div className="mt-0.5 text-[10.5px] font-semibold uppercase tracking-widest text-gray-500 truncate">
                    {agent.role}
                </div>
                <p className="mt-1.5 text-[12.5px] text-gray-600 leading-relaxed line-clamp-2">
                    {agent.tagline}
                </p>
                <div className="mt-2 flex items-center gap-2">
                    <Link
                        to="/app/workforce"
                        className="text-[11px] font-medium text-[#16a34a] hover:text-[#15803d] inline-flex items-center gap-1"
                    >
                        {hired ? "Manage" : "Hire"} <ArrowRight className="w-3 h-3" strokeWidth={2} />
                    </Link>
                </div>
            </div>
        </div>
    );
}
